import React, { useContext, useEffect, useState, useCallback } from "react";
import Router, { useRouter } from "next/router";
import { Formik } from "formik";
import * as Yup from "yup";
import { ApiApi, Login } from "api";
import PrettyButton from "@components/common/PrettyButton";
import { AuthContext } from "@storage/AuthProvider";
import {
  RequestState,
  RequestWrapper,
  useRequest,
} from "@components/common/request";
import { toast } from "react-toastify";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { CompanyRole } from "@helpers/types";
import { useDashboard } from "@helpers/queries/dashboard";
import { useProjectList } from "@helpers/queries/projects";

const LoginForm: React.FC = () => {
  const { setAccessToken, user } = useContext(AuthContext);
  const formSending = useRequest();
  const router = useRouter();

  // After logging in, Company users will be redirected depending on their
  // onboarding status.
  const {
    data: { results: dashboardRuns = [] } = {},
    isLoading: isDashboardLoading,
  } = useDashboard(
    { limit: 20, offset: 0 },
    { enabled: user?.companyRole === CompanyRole.CLIENT },
  );

  const {
    data: { results: projects = [] } = {},
    isLoading: isProjectsLoading,
  } = useProjectList({}, { enabled: user?.companyRole != null });

  const [email, setEmail] = useState("");

  const formikValidationSchema = Yup.object().shape({
    email: Yup.string().required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const onSubmit = useCallback(
    async (formData: Login) => {
      if (!formData.email) {
        toast.error("Email is required");
        return;
      }
      setEmail(formData.email);
      const promise = new ApiApi().apiV1LoginCreate(formData);
      formSending.send(promise, (response) => {
        setAccessToken(response.key);
      });
    },
    [formSending, setAccessToken],
  );

  useEffect(() => {
    if (user?.companyRole) {
      const nextUrl = router.query.next as string;
      if (nextUrl) {
        try {
          const url = new URL(nextUrl, window.location.origin);
          const relativePath = url.pathname + url.search + url.hash;
          if (url.origin === window.location.origin) {
            Router.push(relativePath);
            return;
          }
        } catch {
          // no-op
        }
      }

      switch (user.companyRole) {
        case CompanyRole.CLIENT:
          if (isDashboardLoading || isProjectsLoading) {
            return;
          }
          if (!isDashboardLoading && dashboardRuns.length > 0) {
            Router.push("/dashboard");
            return;
          }

          if (!isProjectsLoading && projects.length === 0) {
            Router.push("/account/new/project-questionnaire");
            return;
          }

          Router.push("/projects");
          break;

        case CompanyRole.TESTER:
          Router.push("/projects");
          break;

        case CompanyRole.ADMIN:
          Router.push("/projects");
          break;

        default:
          Router.push("/dashboard");
          break;
      }
    }
  }, [
    user?.companyRole,
    isDashboardLoading,
    isProjectsLoading,
    projects,
    dashboardRuns.length,
  ]);

  return (
    <>
      <Typography variant="h3">Log in</Typography>
      <br />
      <RequestWrapper context={formSending}>
        <Formik
          initialValues={{
            email,
            password: "",
          }}
          validationSchema={formikValidationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ handleSubmit, handleChange, touched, errors }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  onChange={handleChange}
                  error={touched.email && !!errors.email}
                  helperText={errors.email}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="password"
                  name="password"
                  placeholder="Password"
                  onChange={handleChange}
                  error={touched.password && !!errors.password}
                  helperText={errors.password}
                />
                <Box>
                  <Typography variant="body1">
                    <a href="/account/forgot-password">Forgot your password?</a>
                  </Typography>
                </Box>
                <Box sx={{ paddingTop: 1 }}>
                  <PrettyButton
                    buttonFunction="submit"
                    isLoading={formSending.state === RequestState.Fetching}
                    onClick={handleSubmit}
                    title="Log in"
                  />
                </Box>
              </Stack>
            </form>
          )}
        </Formik>
      </RequestWrapper>
    </>
  );
};

export default LoginForm;
